import type { UtmParams } from '@rover/constants/src/platform/utm.constants';
import { get } from '@rover/react-lib/src/apis/currentUser.api';
import logout from '@rover/react-lib/src/apis/logout.api';
import fetchCurrentUserResponseFactory from '@rover/react-lib/src/factories/fetchCurrentUserResponseFactory';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck/createDuck';
import type {
  ActionCreator,
  Duck,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/src/lib/redux-duck/types';
import type { CurrentUser } from '@rover/rsdk/src/apiClient/latest/model/currentUser';
import type { CurrentUserGeoIpDebug } from '@rover/rsdk/src/apiClient/latest/model/currentUserGeoIpDebug';
import type { CurrentUserViewSchema } from '@rover/rsdk/src/apiClient/latest/model/currentUserViewSchema';
import type { UserInformation } from '@rover/rsdk/src/apiClient/latest/model/userInformation';
import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';
import { getCurrentScope } from '@rover/rsdk/src/modules/ErrorReporting/sentry';
import ObservabilityMetadata from '@rover/rsdk/src/modules/ObservabilityMetadata';
import APIError from '@rover/utilities/APIError';
import { resolveErrorMessage } from '@rover/utilities/resolveApiErrorMessage';

import generateActions from './generateActions';

export type State = {
  errorMessage: string;
  loading: boolean;
  logoutPending: boolean;
  fetchCurrentUserResponse: CurrentUserViewSchema;
};

type Actions = {
  fetchCurrentUserPending: ActionCreator<void>;
  fetchCurrentUserSuccess: ActionCreator<CurrentUserViewSchema>;
  fetchCurrentUserFailure: ActionCreator<string>;
  logoutCurrentUserPending: ActionCreator<void>;
  logoutCurrentUserSuccess: ActionCreator<CurrentUserViewSchema>;
  logoutCurrentUserFailure: ActionCreator<string>;
};

type Effects = {
  fetchCurrentUser: EffectCreator<void>;
  logoutCurrentUser: EffectCreator<{
    nextLocation: string;
    ref?: string;
  }>;
  initSentryUser: EffectCreator<void>;
};

type Selectors = {
  getResponse: SelectorCreator<CurrentUserViewSchema>;
  getCurrentUser: SelectorCreator<UserInformation>;
  getRealUser: SelectorCreator<UserInformation>;
  getGeoIpDebug: SelectorCreator<CurrentUserGeoIpDebug>;
  isLoading: SelectorCreator<boolean>;
  getErrorMessage: SelectorCreator<string>;
  getApiUrl: SelectorCreator<string>;
  getOpk: SelectorCreator<string>;
  isAuthenticated: SelectorCreator<boolean>;
  isBHUser: SelectorCreator<boolean>;
  isImpersonating: SelectorCreator<boolean>;
  isStaffOrImpersonating: SelectorCreator<boolean>;
  getPeopleUrl: SelectorCreator<string>;
  isNewCustomer: SelectorCreator<boolean>;
  getPostalCode: SelectorCreator<string>;
  getUtmParams: SelectorCreator<UtmParams>;
  getFirstHttpReferrer: SelectorCreator<string | null>;
  getIsSsuMigrated: SelectorCreator<boolean>;
  getIsActiveServiceProvider: SelectorCreator<boolean>;
  getIsAnApprovedProvider: SelectorCreator<boolean>;
  getIsIdentityVerificationRequired: SelectorCreator<boolean>;
  getIsBrightHorizonsUser: SelectorCreator<boolean>;
  getIsCmsMigrated: SelectorCreator<boolean>;
  getIsManagementHubMigrated: SelectorCreator<boolean>;
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

export const options: Options = {
  name: 'currentUser',
  initialState: {
    errorMessage: '',
    loading: true,
    logoutPending: false,
    fetchCurrentUserResponse: fetchCurrentUserResponseFactory(),
  },
  actions: { ...generateActions('fetchCurrentUser'), ...generateActions('logoutCurrentUser') },
  effects: {
    fetchCurrentUser:
      () =>
      async (
        dispatch,
        getState,
        { fetchCurrentUserPending, fetchCurrentUserSuccess, fetchCurrentUserFailure },
        { initSentryUser },
        duckSelectors,
        { i18n }
      ) => {
        dispatch(fetchCurrentUserPending());

        try {
          const fetchCurrentUserResponse: CurrentUserViewSchema = await get();
          // this is the earliest and most common spot to set this ObservabilityMetadata prop
          ObservabilityMetadata.set(
            'userOPK',
            (fetchCurrentUserResponse as CurrentUser)?.currentUser.opk
          );
          dispatch(fetchCurrentUserSuccess(fetchCurrentUserResponse));
          dispatch(initSentryUser());
        } catch (e) {
          if ((e as APIError).status === 404) {
            dispatch(fetchCurrentUserSuccess(fetchCurrentUserResponseFactory()));
          } else {
            dispatch(fetchCurrentUserFailure(resolveErrorMessage(e, i18n)));
            captureError(e as APIError);
          }
        }
      },
    logoutCurrentUser:
      ({ nextLocation, ref }) =>
      async (
        // Only call this on the client, never the server
        dispatch,
        getState,
        { logoutCurrentUserPending, logoutCurrentUserSuccess, logoutCurrentUserFailure }
      ) => {
        dispatch(logoutCurrentUserPending());

        try {
          logout({
            nextLocation,
            ref,
            beforeNavCallback: () =>
              dispatch(logoutCurrentUserSuccess(fetchCurrentUserResponseFactory())),
          });
        } catch (e) {
          dispatch(logoutCurrentUserFailure((e as APIError).message));
          captureError(e as APIError);
        }
      },
    initSentryUser:
      () =>
      async (dispatch, getState, actions, effects, { getOpk }) => {
        getCurrentScope().setUser({
          id: getOpk(getState()),
        });
      },
  },
  selectors: {
    getResponse: (getState, createSelector) =>
      createSelector([getState], (x) => x.fetchCurrentUserResponse),
    getCurrentUser: (getState, createSelector, { getResponse }) =>
      createSelector([getResponse], (x) => x.currentUser),
    getRealUser: (getState, createSelector, { getResponse }) =>
      createSelector([getResponse], (x) => x.realUser),
    getGeoIpDebug: (getState, createSelector, { getResponse }) =>
      createSelector([getResponse], (x) => x.geoIpDebug),
    isLoading: (getState, createSelector) => createSelector([getState], (x) => x.loading),
    getErrorMessage: (getState, createSelector) =>
      createSelector([getState], (x) => x.errorMessage),
    getApiUrl: (getState, createSelector, { getCurrentUser }) =>
      createSelector([getCurrentUser], (x) => x.apiUrl),
    getOpk: (getState, createSelector, { getCurrentUser }) =>
      createSelector([getCurrentUser], (x) => x.opk),
    isAuthenticated: (getState, createSelector, { getResponse }) =>
      createSelector([getResponse], (x) => x.authenticated),
    isBHUser: (getState, createSelector, { getCurrentUser }) =>
      createSelector([getCurrentUser], (x) => x.isBrightHorizonsUser),
    isImpersonating: (getState, createSelector, { getRealUser }) =>
      createSelector([getRealUser], (x) => !!x?.opk.length),
    isStaffOrImpersonating: (getState, createSelector, { getCurrentUser, isImpersonating }) =>
      createSelector([getCurrentUser, isImpersonating], (x, y) => x.isStaff || y),
    getPeopleUrl: (getState, createSelector, { getCurrentUser }) =>
      createSelector([getCurrentUser], (x) => `/admin/people/person/?slug=${x.slug}`),
    isNewCustomer: (getState, createSelector, { getCurrentUser }) =>
      createSelector([getCurrentUser], (x) => x.isNewCustomer),
    getPostalCode: (getState, createSelector, { getCurrentUser }) =>
      createSelector([getCurrentUser], (x) => x.postalCode),
    getUtmParams: (getState, createSelector, { getResponse }) =>
      createSelector([getResponse], (x) => x.session?.utmParameters ?? {}),
    getFirstHttpReferrer: (getState, createSelector, { getResponse }) =>
      createSelector([getResponse], (x) => x.session?.firstHttpReferrer || null),
    getIsSsuMigrated: (getState, createSelector, { getCurrentUser }) =>
      createSelector([getCurrentUser], (x) => x.isSsuMigrated),
    getIsActiveServiceProvider: (getState, createSelector, { getCurrentUser }) =>
      createSelector([getCurrentUser], (x) => x.isActiveServiceProvider),
    getIsAnApprovedProvider: (getState, createSelector, { getCurrentUser }) =>
      createSelector([getCurrentUser], (x) => x.isAnApprovedProvider),
    getIsIdentityVerificationRequired: (getState, createSelector, { getCurrentUser }) =>
      createSelector([getCurrentUser], (x) => x.isIdentityVerificationRequired),
    getIsBrightHorizonsUser: (getState, createSelector, { getCurrentUser }) =>
      createSelector([getCurrentUser], (x) => x.isBrightHorizonsUser),
    getIsCmsMigrated: (getState, createSelector, { getCurrentUser }) =>
      createSelector([getCurrentUser], (x) => x.isCmsMigrated),
    getIsManagementHubMigrated: (getState, createSelector, { getCurrentUser }) =>
      createSelector([getCurrentUser], (x) => x.isManagementHubMigrated),
  },
};

export const { actions, selectors, effects, reducer } = createDuck(options) as Duck<
  State,
  Actions,
  Effects,
  Selectors
>;
